<template>
    <div>
        <button type="button" v-if="dmnOptions.isModalMode" class="tw-btn tw-btn--primary tw-btn--lg" v-on:click="openModalWidget()">Buy Tickets</button>
        <div v-if="showTicketWidget" v-bind:class="{ 'tw-modal__container' : dmnOptions.isModalMode }" v-on:click="closeModalWidget()">
            <div v-if="loading"><loading-splash></loading-splash></div>
            <div v-on:click.stop v-bind:class="dmnOptions.isModalMode ? 'tw-modal tw-modal--scroll' : ''">
                <div id="tonic-ticket-widget" class="tw-widget" v-bind:class="{
                        'tw-responsive__xs': containerSize === 'xs', 
                        'tw-responsive__sm': containerSize === 'sm',
                        'tw-responsive__md': containerSize === 'md',
                        'tw-responsive__lg': containerSize === 'lg',
                        'tw-widget__theme--inverse' : dmnOptions.theme === 'inverse'
                    }">

                    <!-- DEFAULT WIDGET VIEW -->
                    <div v-if="dmnOptions.widgetMode === 'default-view'">
                        <widget-header v-if="dmnOptions.displayWidgetHeader"></widget-header>
                        <router-view></router-view>
                        <error-handling v-if="showError" v-bind:errorMessage="errorMessage"></error-handling>
                        <widget-footer></widget-footer>
                    </div>

                    <!-- LIST WIDGET VIEW - no routing -->
                    <div v-if="dmnOptions.widgetMode === 'list-view'" class="tw-accordion-group" id="accordion-top">
                        <widget-header v-if="dmnOptions.displayWidgetHeader"></widget-header>
                        <div v-if="!loading">
                            <error-handling v-if="showError" v-bind:errorMessage="errorMessage"></error-handling>
                            <div v-if="events && events.length > 0" class="tw-accordion">
                                <button type="button" class="tw-accordion-button" :disabled="accordionDisable.disableEvents" v-on:click="accordionDisplay.showEvents = !accordionDisplay.showEvents" v-bind:class="{ 'tw-accordion-button-disabled' : accordionDisable.disableEvents}">
                                    {{selectedEvent ? getTrimmedAccordionTitle(selectedEvent.title) : 'Select event...'}}
                                    <font-awesome-icon class="tw-accordion-button-icon" v-if="!accordionDisplay.showEvents" icon="chevron-right" />
                                    <font-awesome-icon class="tw-accordion-button-icon" v-if="accordionDisplay.showEvents" icon="chevron-down" />
                                </button>
                                <events-list v-show="accordionDisplay.showEvents"></events-list>
                            </div>

                            <div class="tw-accordion">
                                <button type="button" class="tw-accordion-button" :disabled="accordionDisable.disableCalendar" v-on:click="accordionDisplay.showCalendar = !accordionDisplay.showCalendar" v-bind:class="{ 'tw-accordion-button-disabled' : accordionDisable.disableCalendar}">
                                    {{selectedOccurrence ? getTrimmedAccordionTitle(selectedOccurrence.date_friendly) : 'Select date...'}}
                                    <font-awesome-icon class="tw-accordion-button-icon" v-if="!accordionDisplay.showCalendar" icon="chevron-right" />
                                    <font-awesome-icon class="tw-accordion-button-icon" v-if="accordionDisplay.showCalendar" icon="chevron-down" />
                                </button>
                                <calendar-base v-show="accordionDisplay.showCalendar"></calendar-base>
                            </div>

                            <div class="tw-accordion">
                                <button type="button" class="tw-accordion-button" :disabled="accordionDisable.disableTickets" v-on:click="accordionDisplay.showTickets = !accordionDisplay.showTickets" v-bind:class="{ 'tw-accordion-button-disabled' : accordionDisable.disableTickets}">
                                    Select tickets
                                    <font-awesome-icon class="tw-accordion-button-icon" v-if="!accordionDisplay.showTickets" icon="chevron-right" />
                                    <font-awesome-icon class="tw-accordion-button-icon" v-if="accordionDisplay.showTickets" icon="chevron-down" />
                                </button>
                                <tickets-base v-show="accordionDisplay.showTickets"></tickets-base>
                            </div>

                            <checkout v-if="buyNow"></checkout>

                            <widget-footer></widget-footer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang='ts'>
    import { Vue } from "vue-property-decorator";
    import { Component } from "vue-property-decorator";
    import moment from 'moment-timezone';

    import CartManager from './services/CartManager';
    import CurrencyConfig from './constants/CurrencyConfig';
    import DataProviderService from "./services/DataProviderService";
    import Event from "./models/Event";
    import Occurrence from './models/Occurrence';
    import Recurrence from "./classes/Recurrence";
    import TagOptions from "./models/TagOptions";

    // Components
    import CalendarBase from './components/calendar/calendar-base/CalendarBase.vue';
    import Checkout from './components/checkout/Checkout.vue';
    import EventsList from './components/events-list/EventsList.vue';
    import ErrorHandling from './components/misc/error-handling/ErrorHandling.vue';
    import LoadingSplash from './components/misc/loading-splash/LoadingSplash.vue';
    import TicketsBase from './components/tickets/ticket-base/TicketsBase.vue';
    import TicketWidgetComponent from './components/TicketWidgetComponent';
    import WidgetFooter from './components/misc/widget-footer/WidgetFooter.vue';
    import WidgetHeader from './components/misc/widget-header/WidgetHeader.vue';
 
     //fontawesome
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { faChevronLeft, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';

    library.add(faChevronLeft, faChevronRight, faChevronDown);

    @Component({
        components: {
            Checkout,
            CalendarBase,
            EventsList,
            ErrorHandling,
            FontAwesomeIcon,
            LoadingSplash,
            TicketsBase,
            WidgetHeader,
            WidgetFooter
        }
    })

    export default class App extends TicketWidgetComponent {
        public loading: boolean = true;
        public containerSize: string = 'lg';
        public showError: boolean = false;
        public errorMessage: string = '';
        public showTicketWidget: boolean = true;
        public buyNow: boolean = false;
        public accordionDisplay: {
            showCalendar: boolean,
            showEvents: boolean,
            showTickets: boolean,
        } = {
            showCalendar: false,
            showEvents: false,
            showTickets: false,
        }

        public accordionDisable: {
            disableCalendar: boolean,
            disableEvents: boolean,
            disableTickets: boolean,
        } = {
            disableCalendar: true,
            disableEvents: false,
            disableTickets: true,
        }

        mounted() {
            this.createStyleSheet();

            // set app name for the origin
            this.dmnResources.ticketWidget().origin.addDefaultHeader('X-DMN-APP-NAME', 'tonic_partner_ticket_widget');

            this.$root.$data.dataProviderService = new DataProviderService(this.dmnOptions, this.dmnResources);
            this.$root.$data.config.calendarStage = this.$root.$data.stages['calendar'];

            if(this.dmnOptions.isModalMode) {
                this.showTicketWidget = false;
            }

            this.accordionDisplay.showEvents = this.dmnOptions.listViewOpen;

            this.loading = true;

            // if the event ID is set then get the events data
            if(this.dmnOptions.itemIds) {
                this.dataProviderService.getEventsData(this.dmnOptions.itemIds).then((data: any) => {
                    this.handleApiResponse(data);
                });
            }

            // listen for window resize and adjust container size
            window.addEventListener('resize', this._setContainerSize);
            var windowEvent = window.document.createEvent('HTMLEvents'); 
            windowEvent.initEvent('resize', true, false); 
            window.dispatchEvent(windowEvent);

            // set up some currency settings
            (<any>Vue).currencyFilterConfig.fractionCount = 2;
            (<any>Vue).currencyFilterConfig.fractionSeparator = '.';
            (<any>Vue).currencyFilterConfig.symbolSpacing = false;

            if(this.dmnOptions.widgetMode === 'list-view') {
                this.setUpEventListeners();
            }
        }

        /**
        * @desc sets up the custom styles for the widget
        * @param {string} itemIds
        */
        createStyleSheet() {
            let style = document.createElement('style');
            style.type = 'text/css';

            style.innerHTML = `:root { 
                --tonic-primary: #${this.dmnOptions.primary};
                --tonic-primaryLight: #${this.dmnOptions.primaryLight};
                --tonic-secondary: #${this.dmnOptions.secondary};
                --tonic-secondaryLight: #${this.dmnOptions.secondaryLight};
                --tonic-shade1: #${this.dmnOptions.shade1};
                --tonic-shade3: #${this.dmnOptions.shade3}; 
                --tonic-background: #${this.dmnOptions.background}; 
            }`;

            document.getElementsByTagName('head')[0].appendChild(style);
        }

        /**
        * @desc sets up event listeners for the list view state changes
        * @param {string} itemIds
        */
        setUpEventListeners() {
            // add listener for when a date has been selected
            this.$root.$on('dateSelected', (dateSelected: any) => {
                this.$root.$data.selectedOccurrence = dateSelected;

                // show tickets
                this.accordionDisplay.showTickets = true;

                // enable tickets
                this.accordionDisable.disableTickets = false;

                // hide calendar
                this.accordionDisplay.showCalendar = false;
            });

            // add listener for when an event has been selected
            this.$root.$on('eventSelected', (eventSelected: any) => {
                // show calendar
                this.accordionDisplay.showCalendar = true;

                // enable calendar
                this.accordionDisable.disableCalendar = false;

                // hide events tab
                this.accordionDisplay.showEvents = false;

                // disable tickets (if event has switched)
                this.accordionDisable.disableTickets = true;

                // unset the occurrence
                this.selectedOccurrence = false;
            });

            this.$root.$on('buyNow', () => {
                this.buyNow = true;
            });
        }

        /**
        * @desc initialize each event we have gotten back from the API request as an Event object.
        */
        getEventsModel(events: any) {
            let eventModels = [];

            for(let i = 0; i < events.length; i++) {
                let event = new Event(events[i]);

                eventModels.push(event);

                // if we are on the events list and the event has no dates or no tickets then it shouldn't display
                if(events.length > 1 && (event.recurrenceModel.getTotalRemainingDates(moment()) === 0 || event.ticket_types.length === 0) || !event.ticket_types) {
                    eventModels.splice(i, 1);
                }
            }

            return eventModels;
        }

        /**
        * @desc after initial API request, handle where to go based on the data that we have gotten back
        * @param {any} data
        */
        goToNextStage(data: any) {

            // if we have more than one event then go to the events list
            if(data.events.length > 1) {
                this.$router.push({ path: '/events-list' });
            }

            // if we have only one event then go to the calendar
            if(data.events.length === 1) {
                this.$router.push({ path: '/calendar' })
            }
        }

        /**
         * @desc Handles the API response and sets up the $root.$data properties
         * @param {any} - data
         */
        handleApiResponse(data: any) {
            this.loading = false;

            // if there are no events for the events list throw an error
            if(data.events.length === 0) {
                this.throwError(true, 'No dates available for this event');
            }

            // if there is more than one event
            if(data.events.length > 0) {
                this.setRootData(data);

                // if the events should be sorted by a certain method
                this.sortEvents();

                // if we are in default view then handle which stage should be next
                if(this.dmnOptions.widgetMode === 'default-view' && !this.showError) {
                    this.goToNextStage(data);
                }
            }
        }

        /**
         * @desc opens the app if the widget is in modal mode, sets up a listener for when the app closes
         */
        openModalWidget() {
            this.showTicketWidget = true;

            // recieves the close event from the WidgetHeader component
            this.$root.$on('closeModal', () => {
                this.showTicketWidget = false;
            });
        }

        /**
         * @desc emits event that closes the modal
         */
        closeModalWidget() {
            this.$root.$emit('closeModal');
        }

        /**
         * @desc sets the data from the API request to the $root object so it is accessible throughout the application
         * @param {any} 
         */
        setRootData(data: any) {

            // set up events/serverTime/cartManager on the root that can be accessed later
            this.$root.$data.serverTime = moment(data.serverTime);
            this.$root.$data.cartManager = new CartManager(this.dmnOptions);
            this.$root.$data.selectedOccurrences = {};
            this.$root.$data.linkedOccurrences = {};
            this.$root.$data.promotions = {};

            // convert each event returned to us to an Event class object
            this.$root.$data.events = this.getEventsModel(data.events);

            // if presale code is set, set to cart manager
            if(data.presaleCode && data.presaleCode.status !== 'deleted') {
                this.$root.$data.cartManager.presaleCode = data.presaleCode;
                this.$root.$data.dmnOptions.occurrence = data.presaleCode.occurrence;

                this.selectedOccurrence = data.presaleCode.occurrence;
                this.cartManager.presaleCode = data.presaleCode;
            }

            // if we have only one event then go to the calendar
            if(data.events.length === 1) {
                this._handleSingleEventResponse(data);
            }
        }

        sortEvents() {
            if(this.dmnOptions.sortBy === 'title') {
                this.$root.$data.events.sort((a:any, b:any) => a.title < b.title ? -1 : a.title > b.title ? 1 : 0);
            }
    
            if(this.dmnOptions.sortBy === 'next-date') {
                this.$root.$data.events.sort((a:any, b:any) => {
                    if (a.recurrenceModel.getNextDate(moment().subtract(1, 'days'), '', a.start_time) < b.recurrenceModel.getNextDate(moment().subtract(1, 'days'), '', b.start_time)) {
                        return -1;
                    }

                    if (a.recurrenceModel.getNextDate(moment().subtract(1, 'days'), '', a.start_time) > b.recurrenceModel.getNextDate(moment().subtract(1, 'days'), '', b.start_time)) {
                        return 1;
                    }
                    
                    // sort by title if dates are the same
                    return a.title < b.title ? -1 : a.title > b.title ? 1 : 0;
                });
            }
        }

        /**
         * @desc populates the error component with data
         * @param {boolean} - showError
         * @param {string} - errorMessage
         */
        throwError(showError: boolean, errorMessage: string) {
            this.showError = showError;
            this.errorMessage = errorMessage;
        }

        /**
        * @desc updates the responsive container
        */
        _setContainerSize() {
            this.dmnOptions.widgetMode === 'list-view' ? this.containerSize = 'sm' : this.containerSize = this.$appSize();
        }

        /**
        * @desc gets the title for an accordion tab and shortens it when needed
        */
        getTrimmedAccordionTitle(title: string) {
            let size: any = {
                xs: 25,
                sm: 40,
                md: 70,
                lg: 200
            }

            if(title && title.length > size[this.$appSize()]) {
                let trimmedString = title.substr(0,  size[this.$appSize()]);

                // don't split in middle of word
                trimmedString = trimmedString.substr( 0, Math.min(trimmedString.length, trimmedString.lastIndexOf(" ")));
                
                return trimmedString + '...';
            }

            return title;
        }

        /**
         * @desc handle the apps response to a single event
         * @param {any} data
         */
        _handleSingleEventResponse(data: any) {
            // we dont want the list view if there is only one event
            this.dmnOptions.widgetMode = 'default-view';

            // remove the events_list navigation breadcrumb
            delete this.$root.$data.stages['events_list'];

            // add the event to the root and to services
            this.$root.$data.selectedEvent = this.events[0];
            this.$root.$data.cartManager.event = this.events[0];
            this.$root.$data.dataProviderService.selectedEvent = this.events[0];

            // set currency to the events currency
            (<any>Vue).currencyFilterConfig.symbol = (<any>CurrencyConfig.currencies)[data.events[0].currency].symbol;

            // if occurrences are set, its a single date, then go straight to ticket stage
            if(data.occurrences) {
                for(let key in data.occurrences) {                        
                    this.$root.$data.selectedOccurrence = new Occurrence(data.events[0]._id, key, data.occurrences[key]);
                    this.$root.$data.selectedOccurrence.serverTime = this.serverTime;
                    this.$root.$data.cartManager.occurrence = this.selectedOccurrence;
                }

                // remove the calendar navigation breadcrumb
                delete this.$root.$data.stages['calendar'];
            }
            
            // if there are no ticket types available display error
            if(this.selectedEvent.ticket_types.length === 0) {
                this.throwError(true, 'There are no tickets available to purchase!')
            }
        }
    }
</script>

<style lang="scss" src="./assets/scss/app.scss"></style>